import React, { useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import Loader from 'components/D3Loader'
import { browserHistory } from 'utils/navigation'
import { getStudyVersionID } from 'utils/studyConfigUtils'
import StudyTimelinePage from '../components/StudyTimelinePage'
import { actions } from '../modules/StudyTimeline'
import { getStudyDurationForTimeline, getStudyDurationFromStudy } from 'utils/studyDurationForTimeline'

const mapStateToProps = (state, ownProps) => {
  const { params } = ownProps
  const { studyID: routeStudyID, studyVersionID: routeStudyVersionID } = params || {}
  const { studyTimeline = {}, study = {}, loading = {}, location } = state
  const { currentStudy = {} } = study
  const derivedStudyID = currentStudy?.id
  const studyVersionID = routeStudyVersionID ?? getStudyVersionID(location) ?? null

  return {
    // Prefer route param so study change is detected immediately when URL changes (avoids stale cached data)
    // Use null instead of undefined to avoid "Cannot read properties of undefined (reading 'toString')" in consumers
    studyID: routeStudyID ?? derivedStudyID ?? null,
    studyVersionID,
    currentStudy: currentStudy || {},
    studyConfig: currentStudy?.config || {},
    loading,
    studyTimeline: studyTimeline || {},
  }
}

const mapDispatchToProps = {
  ...actions,
}

const StudyTimelinePageContainer = props => {
  const {
    loading,
    studyID,
    studyVersionID,
    currentStudy,
    fetchVisitSchedules,
    fetchInstruments,
    resetStudyTimeline,
    setStudyDurationFromList,
  } = props
  const previousStudyIDRef = useRef(null)

  useEffect(() => {
    // Reset state when study changes or on first mount (clear any cached data from another study)
    const studyChanged = previousStudyIDRef.current !== null && previousStudyIDRef.current !== studyID
    const initialMountWithStudy = previousStudyIDRef.current === null && studyID
    if (studyChanged || initialMountWithStudy) {
      resetStudyTimeline()
    }
    previousStudyIDRef.current = studyID

    // Study duration from session (set when clicking study card) or currentStudy fallback (direct nav, visits disabled)
    const studyDuration = getStudyDurationForTimeline() || getStudyDurationFromStudy(currentStudy)
    if (studyDuration) {
      setStudyDurationFromList(studyDuration)
    }

    if (studyID) {
      const hasVisitSupport = currentStudy?.config?.visit_support != null
      if (hasVisitSupport) {
        fetchVisitSchedules(studyID)
      }
      fetchInstruments(studyID)
    }
  }, [studyID, currentStudy, fetchVisitSchedules, fetchInstruments, resetStudyTimeline, setStudyDurationFromList])

  // Navigate only — instrumentReducer is not available on Study Timeline. Scheduler route
  // injects it, fetches the instrument, then applies scheduleIndex from the query string.
  const onEditSchedule = useCallback(
    (instrumentId, scheduleIndex) => {
      if (!studyID || !studyVersionID || instrumentId == null) return
      const index = Number.isInteger(scheduleIndex) ? scheduleIndex : 0
      browserHistory.push(
        `/studies/${studyID}/${studyVersionID}/instruments/${instrumentId}/schedule?scheduleIndex=${index}`,
      )
    },
    [studyID, studyVersionID],
  )

  if (loading.general || loading.studyTimeline || loading.studyTimelineInstruments) {
    return <Loader />
  }

  return <StudyTimelinePage {...props} onEditSchedule={onEditSchedule} />
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(StudyTimelinePageContainer)
